import { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../AuthContext';
import { API_BASE_URL } from '../config';
import { buildScopeQuery } from './timeScopes';

// DJB-142 / DJB-147: fetches a /statistics endpoint for the selected
// time-scope preset and re-queries whenever the scope changes. `path` may
// already carry a query string (e.g. ?limit=25); the from= param is appended.
// DJB-195 r2: `enabled: false` switches the query off (no fetch, no data) —
// the Transactions graph skips its series query for the Lifetime scope.
export const useScopedStatistics = (path, scopeKey, { enabled = true } = {}) => {
  const { authData } = useContext(AuthContext) || {};
  const accessToken = authData?.accessToken;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setLoading(false);
      setError(null);
      return undefined;
    }
    if (!accessToken) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    const separator = path.includes('?') ? '&' : '?';
    fetch(`${API_BASE_URL}${path}${separator}${buildScopeQuery(scopeKey)}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch statistics (${response.status})`);
        }
        return response.json();
      })
      .then((body) => {
        if (!cancelled) {
          setData(body);
          setLoading(false);
        }
      })
      .catch((fetchError) => {
        console.error(`Failed to fetch ${path}:`, fetchError);
        if (!cancelled) {
          setError(fetchError.message);
          setLoading(false);
        }
      });

    // Ignore responses that land after the scope changed again
    return () => {
      cancelled = true;
    };
  }, [path, scopeKey, accessToken, enabled]);

  return { data, loading, error };
};
